import { ErrorMessage } from '@/shared/utils';
import {
  Either,
  fetchData,
  isLeft,
  isRight,
  left,
  right,
  TaskEither,
  taskEither,
} from '@shared-commons/utils';
import { Subscription } from '../types';
import { updateCustomerSubscriptionValidator } from './update-customer-subscription-validator';
import { updateCustomerSubscriptionAdapter } from './update-customer-subscription-adapter';

export const cancelCustomerSubscription = ({
  email,
  accessToken,
  userUid,
}: {
  email: string | undefined;
  userUid: string;
  accessToken: string | null;
}): TaskEither<Error, Subscription> => {
  const validateCustomerSubscription = (
    subscription: Subscription,
  ): Either<Error, Subscription> => {
    const validatedCustomerSubscription = updateCustomerSubscriptionValidator(subscription);
    if (isLeft(validatedCustomerSubscription)) {
      const errorMessage = new Error(validatedCustomerSubscription.value.message);
      return left(new Error(ErrorMessage('Validation error') + errorMessage));
    }
    return right(validatedCustomerSubscription.value);
  };

  const adapter = (input: Subscription): TaskEither<Error, Subscription> => {
    const adaptTask: TaskEither<Error, Subscription> = async () => {
      try {
        const adaptedCustomerSubscription = await updateCustomerSubscriptionAdapter(input)();
        if (isRight(adaptedCustomerSubscription)) {
          return right(adaptedCustomerSubscription.value);
        } else {
          throw new Error(adaptedCustomerSubscription.value.message);
        }
      } catch (error) {
        return left(
          new Error(ErrorMessage('Failed to adapt users') + ': ' + (error as Error).message),
        );
      }
    };

    return taskEither(adaptTask);
  };

  return fetchData<Subscription>(
    `${process.env.NEXT_PUBLIC_HOST}/api/customer/subscription/cancel`,
    (input) => adapter(input),
    validateCustomerSubscription,
    'POST',
    {
      Authorization: `Bearer ${accessToken}`,
    },
    JSON.stringify({
      email,
      userUid,
      redirectUrl: typeof window !== 'undefined' ? window.location.href : '',
    }),
  );
};
